import React, { useState, useEffect } from 'react'
import { arrayOf, bool, number, object, string } from 'prop-types'

import BaseTemplate from 'components/templates/BaseTemplate'

import Container from 'components/atoms/Container'
import Row from 'components/atoms/Row'
import Col from 'components/atoms/Col'
import Button from 'components/atoms/Button'
import Typography from 'components/atoms/Typography'

import ThumbnailLoader from 'components/molecules/ThumbnailLoader'

import CampusPerfectCard from 'components/organisms/CampusPerfectCard'

import Select from 'react-select'

import { branch } from 'baobab-react/higher-order'
import formatToLocalTimezone from 'utils/formatToLocalTimezone'
import API from 'utils/API'
import storage from 'utils/storage'
import config from 'config'

const { assets_url: assetsUrl } = config

const CTAButton = ({ href, disabled }) => (
  <Button
    component="button"
    type="button"
    className={`test-cta ${disabled && 'disabled'}`}
    href={disabled ? '#' : href}
    disabled={disabled}
  >
    { disabled ? 'Segera Hadir' : 'Coba Sekarang' }
    <style jsx global>
      {`
        .test-cta {
          width: 100%;
          font-weight: bold;
          border: none;
          &.disabled {
            background-color: #bed2e8;

            &:hover {
              background-color: #bed2e8;
              cursor: default;
            }
          }
        }
      `}
    </style>
  </Button>
)

CTAButton.propTypes = {
  href: string,
  disabled: bool,
}

const TestsDirectory = ({ tests, page, perPage, isLastPage }) => {
  const [firstLoad, setFirstLoad] = useState(true)
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState({
    page: page || 1,
    per_page: perPage || 12,
    type: '',
    sort: '',
  })

  const filterOptions = [
    {
      value: 'potention',
      label: 'Uji Potensi',
    },
    {
      value: 'tryout',
      label: 'Try Out',
    },
    {
      value: 'exam',
      label: 'Ujian',
    },
  ]

  const sortOptions = [
    {
      value: '',
      label: 'Terbaru',
    },
    {
      value: 'popularity DESC',
      label: 'Terpopuler',
    },
    {
      value: 'label ASC',
      label: 'Gratis',
    },
    {
      value: 'label DESC',
      label: 'Berbayar',
    },
  ]

  const updateQuery = (newQuery) => {
    setQuery({ ...query, ...newQuery })
  }

  const loadMore = () => {
    setQuery({
      ...query,
      page: query.page + 1,
    })
  }

  const handleFilterChange = (e) => {
    e ? updateQuery({ type: e.value, page: 1 }) :
      updateQuery({ type: '', page: 1 })
  }

  const handleSortChange = (e) => {
    e ? updateQuery({ sort: e.value, page: 1 }) :
      updateQuery({ sort: '', page: 1 })
  }

  const fetchTests = () => {
    setLoading(true)
    setTimeout(() => {
      API.get('/tests.json', query)
      .then(({ data }) => {
        const serverProps = storage.select('serverProps')
        const updatedData = {
          page: data.params.page,
          perPage: data.params.perPage,
          isLastPage: data.isLastPage,
          tests: data.params.page > 1 ?
            [...tests, ...data.tests] :
            [...data.tests],
        }
        serverProps.merge(updatedData)
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
    }, 500)
  }

  useEffect(() => {
    if (firstLoad) {
      setFirstLoad(false)
    } else {
      fetchTests()
    }
  }, [query])

  const renderCategory = (label, period, isComingSoon) => (
    <div className={`test-label-period ${isComingSoon && 'test-label--disabled'}`}>
      {
        (label !== 'none') &&
        <span className="test-label-period__label">
          { label === 'free' && 'Gratis'}
          { label === 'paid' && 'Berbayar'}
        </span>
      }
      <span className="test-label-period__period">
        {period}
      </span>
      <style jsx>
        {`
          .test-label-period {
            &__label {
              color: #fff;
              font-size: 12px;
              background: #fc7d20;
              padding: 4px 16px;
              border-radius: 4px;
              margin-right: 8px;
            }
            &__period {
              color: #00beff;
            }
            &--disabled {
              .test-label-period__label {
                background-color: #bed2e8;
              }
              .test-label-period__period {
                color: #bed2e8;
              }
            }
          }
        `}
      </style>
    </div>
  )

  return (
    <BaseTemplate useNewHeader>
      <div className="tests-page">
        <div className="tests-page__banner">
          <a
            href="https://www.quipper.com/id/video/?utm_source=QCampus&utm_medium=referral&utm_campaign=QCtoQV&utm_content=quipper_video#pricing"
            target="_blank"
            rel="noopener"
          >
            <picture>
              <source media="(min-width: 768px)" srcSet={`${assetsUrl}/assets/img/quipper_video/header_PC.png`} />
              <img src={`${assetsUrl}/assets/img/quipper_video/header_SP.png`} className="img-responsive" alt="Quipper Video Banner" />
            </picture>
          </a>
        </div>
        <div className="tests-page__heading">
          <Container>
            <Typography component="h1" variant="headline">
              Direktori Uji Potensi
            </Typography>
            <div className="tests-page__filter-n-sort">
              {/* filter by type: exam, potention, tryout */}
              <Row>
                <Col md="4">
                  <Select
                    className="tests-page__filter-opts"
                    placeholder="Tampilkan semua tipe ujian"
                    options={filterOptions}
                    onChange={handleFilterChange}
                    isClearable
                  />
                </Col>
                <Col md="4">
                  {/* latest, popular, free, and paid */}
                  <Select
                    className="aptitude-history__sort-opts"
                    placeholder="Urutkan"
                    options={sortOptions}
                    onChange={handleSortChange}
                    isClearable
                  />
                </Col>
              </Row>
            </div>
          </Container>
        </div>
        <Container>
          <div className="tests-page__list">
            <Row>
              {
                (query.page === 1 && !loading) || (query.page > 1) ? tests.map((test, idx) => {
                  const periodText = test.limitedPeriod ?
                    `${formatToLocalTimezone(test.startDate, 'DD MMM YYYY')} - ${formatToLocalTimezone(test.endDate, 'DD MMM YYYY')}` :
                    'Periode: Terbuka'
                  const isComingSoon = test.status === 'not_started'
                  return (
                    <Col md="4" key={`test-itm-${test.id}`}>
                      <CampusPerfectCard
                        dataTestId={`dir-test-card-${idx}`}
                        kind="tests"
                        category={
                          renderCategory(test.label, periodText, isComingSoon)
                        }
                        description={test.description}
                        href={`/tests/${test.slug}`}
                        title={test.name}
                        thumbnail={test.image}
                        linkRel={test.action !== 'url' ? 'nofollow' : undefined}
                        recordLead={test.record_lead}
                        footerContent={<CTAButton href={test.actionUrl} disabled={isComingSoon}/>}
                        openNewTab={test.action === 'url'}
                        disabled={isComingSoon}
                        withFooter
                        authRequired
                      />
                    </Col>
                  )
                }) : ''
              }
            </Row>
            { loading && <ThumbnailLoader /> }
            {
              !isLastPage &&
              <div className="tests-page__pagination">
                <Button component="button" variant="link" onClick={loadMore}>Lihat Uji Potensi Lainnya</Button>
              </div>
            }
          </div>
        </Container>
      </div>
      <style jsx>
        {`
          .tests-page {
            &__heading {
              padding: 16px 0 24px;
              background-color: #ebeef3;
            }
            :global(.campus-perfect-card__logo) {
              display: none;
            }
            :global(.campus-perfect-card__identity) {
              height: 48px;
            }
            :global(.campus-perfect-card__desc) {
              min-height: 70px;
            }
            :global(.campus-perfect-card__see-more) {
              display: none;
            }
            &__filter-n-sort {
              margin-top: 16px;
            }
            &__list {
              margin-top: 24px;
              margin-bottom: 32px;
              :global(.cmps-row) {
                margin-bottom: 16px;
              }
            }
            &__pagination {
              display: flex;
              justify-content: center;
              margin-top: 16px;
              :global(button) {
                color: #7d92a9;
                background-color: transparent;
                border: none;
                font-weight: bold;
                font-size: 16px;
              }
            }
          }
        `}
      </style>
    </BaseTemplate>
  )
}


TestsDirectory.propTypes = {
  tests: arrayOf(object),
  page: number,
  perPage: number,
  isLastPage: bool,
}

TestsDirectory.defaultProps = {
  tests: [],
  page: 1,
  perPage: 12,
}

export { TestsDirectory }

export default branch({
  tests: ['serverProps', 'tests'],
  page: ['serverProps', 'page'],
  perPage: ['serverProps', 'perPage'],
  isLastPage: ['serverProps', 'isLastPage'],
}, TestsDirectory)
